/**
 * Distillation engine for the Vulnerability Immunity Distillation demo (RD-65).
 *
 * Models the VID iteration as a deterministic, monotone endofunctor on the
 * inclusion lattice of generator subsets of gl(n,ℂ). The generators are REAL
 * gl(n,ℂ) elements (`ComplexMatrix`) and the per-generator predicate is computed
 * live on the @csk/core Mₙ(ℂ) realization — there are no abstract compliance
 * booleans. The engine is dimension-general: the presets span gl(2,ℂ) and
 * gl(3,ℂ) and run through the very same membership predicates.
 *
 * The SOLE violable predicate is **A1: exp(X) ∈ H**, where H ⊆ GL(n,ℂ) is the
 * authority subgroup the operator supplies. One distillation STEP strips the
 * FIRST A1-violating generator (deterministic order by id), so μ descends one
 * per step and the lab shows step-by-step well-founded descent. A2/A3/A4 hold
 * for every gl(n,ℂ) generator by construction (`structuralAxiomsHold`), so they
 * are never distillation targets — distillation strips exactly the A1 violators.
 *
 * Companion to agda/AttackSurfaceConvergence/ImmunityDistillation.agda.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import {
  cExp,
  cMod,
  cSub,
  cmatDet,
  cmatEigenvalues,
  cmatExp,
  cmatTrace,
  toTuples,
  type Complex,
  type ComplexMatrix,
} from "@csk/core/linear";
import type {
  AuthoritySubgroup,
  DistillationFrame,
  DistillationTrace,
  Generator,
} from "./types.js";

export type {
  AuthoritySubgroup,
  Complex,
  ComplexMatrix,
  DistillationFrame,
  DistillationTrace,
  Generator,
  GeneratorId,
  GeneratorSet,
} from "./types.js";

export { SPECTRAL_SEPARATION_BUILT_IN } from "./types.js";

/** Membership tolerance for the authority-subgroup predicates and the A4 residual. */
export const MEMBERSHIP_TOLERANCE = 1e-6;

/** The gl(n,ℂ) element's group image exp(X) ∈ GL(n,ℂ). */
export function expOf(gen: Generator): ComplexMatrix {
  return cmatExp(gen.matrix);
}

/**
 * Whether exp(X) lies in the operator-supplied authority subgroup H (the A1
 * predicate). Each H is a computable membership predicate on the group image,
 * defined for any dimension n:
 *
 *   - `"upper-triangular"`: every below-diagonal entry of exp(X) vanishes.
 *   - `"special-linear"`: det(exp X) = 1.
 *   - `"diagonal"`: every off-diagonal entry of exp(X) vanishes.
 */
export function inAuthoritySubgroup(
  H: AuthoritySubgroup,
  expX: ComplexMatrix,
): boolean {
  const tol = MEMBERSHIP_TOLERANCE;
  const rows = toTuples(expX);
  switch (H) {
    case "upper-triangular":
      return rows.every((row, i) =>
        row.every((z, j) => j >= i || cMod(z) < tol),
      );
    case "diagonal":
      return rows.every((row, i) =>
        row.every((z, j) => i === j || cMod(z) < tol),
      );
    case "special-linear": {
      const one: Complex = [1, 0];
      return cMod(cSub(cmatDet(expX), one)) < tol;
    }
  }
}

/**
 * A1 — the SOLE violable predicate. A generator is immune iff its group image
 * exp(X) lands inside the operator's authority subgroup H. A2/A3/A4 hold for
 * every gl(2,ℂ) generator by construction (`structuralAxiomsHold`), so the A1
 * verdict alone decides immunity.
 */
export function isImmune(gen: Generator, H: AuthoritySubgroup): boolean {
  return inAuthoritySubgroup(H, expOf(gen));
}

/**
 * The Jacobi residual |det(exp X) − exp(tr X)| — the A4 master-identity gap.
 * Zero (up to tolerance) for every gl(2,ℂ) generator; surfaced so the lab and
 * tests can SHOW A4 holds rather than assert it.
 */
export function jacobiResidual(gen: Generator): number {
  return cMod(cSub(cmatDet(cmatExp(gen.matrix)), cExp(cmatTrace(gen.matrix))));
}

/**
 * A2/A3/A4 hold for every gl(2,ℂ) generator by construction. This helper
 * checks them LIVE so the UI and tests can demonstrate, not merely claim, the
 * structural axioms:
 *
 *   - A2 (spectral separation): `cmatEigenvalues` returns n eigenvalues —
 *     the eigendecomposition exists over ℂ.
 *   - A3 (temporal flow): the exp semigroup law holds for all X (T3 carrier);
 *     exp(X) is well-defined (no non-finite entries).
 *   - A4 (resource master identity): the Jacobi residual det(exp X) − exp(tr X)
 *     vanishes within tolerance.
 *
 * Returns `true` for every gl(n,ℂ) generator — there is no X for which the
 * structural axioms fail.
 */
export function structuralAxiomsHold(gen: Generator): boolean {
  // A2 (spectral separation): the eigendecomposition exists over ℂ. We compute
  // the n eigenvalues and confirm each is a finite complex number — the live
  // demonstration that `SPECTRAL_SEPARATION_BUILT_IN` holds for this generator,
  // not a static assumption.
  const eigenvalues = cmatEigenvalues(gen.matrix);
  const a2 = eigenvalues.every(
    (z) => Number.isFinite(z[0]) && Number.isFinite(z[1]),
  );
  const expX = toTuples(cmatExp(gen.matrix));
  const a3 = expX
    .flat()
    .every((z) => Number.isFinite(z[0]) && Number.isFinite(z[1]));
  const a4 = jacobiResidual(gen) < MEMBERSHIP_TOLERANCE;
  return a2 && a3 && a4;
}

/** The attack surface μ(ALLOW): the generator count of the set. */
export function attackSurface(set: readonly Generator[]): number {
  return set.length;
}

/**
 * The first A1-violating generator (deterministic order by id), or `null` if
 * every surviving generator is immune. This is the generator the next
 * distillation step will annihilate.
 */
export function nextViolator(
  set: readonly Generator[],
  H: AuthoritySubgroup,
): Generator | null {
  const ordered = [...set].sort((a, b) => a.id.localeCompare(b.id));
  for (const gen of ordered) {
    if (!isImmune(gen, H)) return gen;
  }
  return null;
}

/**
 * A single distillation step. Strips the FIRST A1-violating generator
 * (exp(X) ∉ H) in canonical id order, so μ descends exactly one per step and
 * the lab shows step-by-step well-founded descent. If the set is already
 * immune it is a fixed point and is returned unchanged.
 */
export function distillStep(
  set: readonly Generator[],
  H: AuthoritySubgroup,
): readonly Generator[] {
  const violator = nextViolator(set, H);
  if (violator === null) return set;
  return set.filter((gen) => gen.id !== violator.id);
}

/**
 * Run distillation to its fixed point, recording every frame.
 *
 * Termination is guaranteed by well-founded descent on the generator count μ:
 * each step that is not already at the fixed point strips exactly one
 * generator, so μ strictly decreases and the iteration halts. The fixed point
 * IMMUNE(ALLOW₀) contains exactly the A1-compliant generators of ALLOW₀ (least
 * fixed point / Knaster–Tarski containment: minimal, nothing removed
 * unnecessarily).
 */
export function distillToFixedPoint(
  set: readonly Generator[],
  H: AuthoritySubgroup,
): DistillationTrace {
  const frames: DistillationFrame[] = [
    {
      step: 0,
      generators: set,
      stripped: [],
      attackSurface: attackSurface(set),
    },
  ];

  let current = set;
  // Each step strips exactly one generator, so this loop runs at most
  // `set.length` times before reaching the fixed point.
  for (let guard = 0; guard <= set.length; guard += 1) {
    const next = distillStep(current, H);
    if (next.length === current.length) break;
    const survivors = new Set(next.map((gen) => gen.id));
    const stripped = current.filter((gen) => !survivors.has(gen.id));
    frames.push({
      step: frames.length,
      generators: next,
      stripped,
      attackSurface: attackSurface(next),
    });
    current = next;
  }

  return {
    frames,
    immune: current,
    steps: frames.length - 1,
    alreadyImmune: frames.length === 1,
  };
}

/**
 * The immune subset of a generator set, computed directly: the A1-compliant
 * generators (exp(X) ∈ H). By the confluence of distillation this equals
 * `distillToFixedPoint(set, H).immune` as a set.
 */
export function immuneSubset(
  set: readonly Generator[],
  H: AuthoritySubgroup,
): readonly Generator[] {
  return set.filter((gen) => isImmune(gen, H));
}
